import { useEffect, useMemo, useState } from "react";
import { AdminMap, type AdminMapMode, type MapLocation } from "@/components/admin/AdminMap";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSnowMapSessions } from "@/hooks/useAdminDashboardData";
import { buildSnowMapLocations } from "@/lib/snowMapStatus";

function startOfTodayLocal(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function toDatetimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function parseDatetimeLocal(value: string): Date {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return startOfTodayLocal();
  return parsed;
}

interface AdminDashboardMapCardProps {
  cleaningLocations: MapLocation[];
  locationsWithoutCoords: { id: string; address: string }[];
  isSnowSeason: boolean;
  orgId: string;
  onDetails: (id: string) => void;
}

export function AdminDashboardMapCard({
  cleaningLocations,
  locationsWithoutCoords,
  isSnowSeason,
  orgId,
  onDetails,
}: AdminDashboardMapCardProps) {
  const [mapMode, setMapMode] = useState<AdminMapMode>("cleaning");
  const [cutoffInput, setCutoffInput] = useState(() => toDatetimeLocalValue(startOfTodayLocal()));
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  const cutoff = useMemo(() => parseDatetimeLocal(cutoffInput), [cutoffInput]);
  const snowQueryEnabled = isSnowSeason && mapMode === "snow";
  const { data: snowSessions } = useSnowMapSessions(orgId, cutoff, snowQueryEnabled);

  useEffect(() => {
    if (!isSnowSeason && mapMode === "snow") {
      setMapMode("cleaning");
      setSelectedLocationId(null);
    }
  }, [isSnowSeason, mapMode]);

  const snowLocations = useMemo(() => {
    const bases = cleaningLocations.map((loc) => ({
      id: loc.id,
      address: loc.address,
      latitude: loc.latitude,
      longitude: loc.longitude,
    }));
    if (!snowSessions) {
      return bases.map((loc) => ({
        ...loc,
        status: "gray" as const,
        currentWorkerName: null,
        nextTaskName: null,
        taskProgress: null,
        lastCompletedAt: null,
      }));
    }
    return buildSnowMapLocations(
      bases,
      snowSessions.sessions,
      cutoff,
      snowSessions.workerNameById
    );
  }, [cleaningLocations, snowSessions, cutoff]);

  const displayedLocations = mapMode === "snow" ? snowLocations : cleaningLocations;

  const handleModeChange = (value: string) => {
    const next = value === "snow" ? "snow" : "cleaning";
    setMapMode(next);
    setSelectedLocationId(null);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Mapa obiektów</CardTitle>
            <CardDescription>
              {mapMode === "snow"
                ? "🟢 Odśnieżono po wybranej godzinie · 🟡 W toku · ⚪ Brak odśnieżania po wybranej godzinie"
                : "🔴 Zaległe · 🟡 W toku · 🟢 Zakończone · ⚪ Brak zadań na dziś"}
            </CardDescription>
          </div>
          {isSnowSeason && (
            <Tabs value={mapMode} onValueChange={handleModeChange}>
              <TabsList>
                <TabsTrigger value="cleaning">Sprzątanie</TabsTrigger>
                <TabsTrigger value="snow">Odśnieżanie</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>
        {mapMode === "snow" && (
          <div className="mt-3 max-w-sm space-y-2">
            <Label htmlFor="snow-map-cutoff">Odśnieżono po</Label>
            <Input
              id="snow-map-cutoff"
              type="datetime-local"
              value={cutoffInput}
              onChange={(e) =>
                setCutoffInput(e.target.value || toDatetimeLocalValue(startOfTodayLocal()))
              }
            />
            <p className="text-xs text-muted-foreground">
              Zielony = odśnieżono po wybranej godzinie. Sesje wcześniejsze nie liczą się.
            </p>
          </div>
        )}
        {locationsWithoutCoords.length > 0 && (
          <div className="mt-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-sm">
            <p className="font-medium text-amber-800 dark:text-amber-200">
              Nieruchomości bez lokalizacji GPS (nie wyświetlają się na mapie):
            </p>
            <ul className="mt-1 list-disc list-inside text-amber-700 dark:text-amber-300 space-y-0.5">
              {locationsWithoutCoords.map((loc) => (
                <li key={loc.id}>
                  <button
                    type="button"
                    className="underline hover:no-underline text-left"
                    onClick={() => onDetails(loc.id)}
                  >
                    {loc.address}
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-amber-600 dark:text-amber-400 text-xs">
              Uzupełnij współrzędne w szczegółach nieruchomości, aby pojawiła się na mapie.
            </p>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <AdminMap
          mode={mapMode}
          locations={displayedLocations}
          selectedLocationId={selectedLocationId}
          onMarkerClick={(id) => setSelectedLocationId(id)}
          onClosePreview={() => setSelectedLocationId(null)}
          onDetails={(id) => {
            setSelectedLocationId(null);
            onDetails(id);
          }}
          className="h-[360px]"
        />
      </CardContent>
    </Card>
  );
}
